'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import { trackInvitacionGrupoPostHog } from '@/lib/posthogAnalytics';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { getInstagramOptimizations, isMobileDevice } from '@/lib/instagramBrowserDetection';
import MuxVideoPlayerWithCTA from '@/components/MuxVideoPlayerWithCTA';
import {
  BRAND_PERSON_NAME,
  BRAND_SITE_URL,
  BRAND_OG_IMAGE_URL,
} from "@/lib/brandConfig";
import {
  getStaticPageFromPathname,
  getPagePathForStaticPage,
  type StaticPageVideoPage,
} from '@/config/staticPageVideoConfig';

/** Default static page when pathname does not match (fallback). */
const DEFAULT_STATIC_PAGE: StaticPageVideoPage = 'grupo-privado';

const InvitacionGrupo = () => {
  const { pathname } = useLocation();
  const instagramOpts = getInstagramOptimizations();

  const page: StaticPageVideoPage =
    getStaticPageFromPathname(pathname) ?? DEFAULT_STATIC_PAGE;
  const pagePath = getPagePathForStaticPage(page);

  const seoConfig = useMemo(
    () => ({
      title: `Telegram Groups | ${BRAND_PERSON_NAME}`,
      description: `Unite a los grupos de Telegram de ${BRAND_PERSON_NAME}. Contenido exclusivo y comunidad premium.`,
      keywords: `${BRAND_PERSON_NAME}, Telegram groups, grupos Telegram, contenido exclusivo, comunidad premium, ${BRAND_PERSON_NAME}`,
      ogImage: BRAND_OG_IMAGE_URL,
      canonical: `${BRAND_SITE_URL}${pagePath}`,
      robots: 'index, follow' as const,
    }),
    [pagePath]
  );
  useSEO(seoConfig);

  // Track page view
  useEffect(() => {
    trackInvitacionGrupoPostHog('view', { page_path: pagePath });
  }, [pagePath]);

  // Screenshot and recording protection. We track violations silently (PostHog + DB + email
  // for critical only). We never show popups, toasts, or "can't screenshot/record" messages to users.
  useEffect(() => {
    const effectivePagePath = pagePath;
    const isMobile = isMobileDevice();
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as unknown as { MSStream?: unknown }).MSStream;
    const isAndroid = /Android/.test(navigator.userAgent);
    
    // Cache for geo data to avoid multiple API calls
    let geoDataCache: Record<string, unknown> | null = null;
    let geoDataPromise: Promise<Record<string, unknown>> | null = null;

    // Buffer critical violations for 3-5 minutes; send one consolidated email per user session.
    // This groups multiple attempts into a single report instead of spamming emails.
    const EMAIL_REPORT_BUFFER_MS = 180_000; // 3 minutes - wait to group multiple attempts
    const EMAIL_GRACE_PERIOD_MS = 90_000; // No emails for first 90s after page load (avoid false positives on load)
    const EMAIL_COOLDOWN_BY_TYPE_MS = 300_000; // 5 min cooldown per violation type
    const pageLoadTime = Date.now();
    const initialWindowSize = {
      outerWidth: window.outerWidth,
      outerHeight: window.outerHeight,
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight,
    };
    const emailReportBuffer: Record<string, unknown>[] = [];
    let emailReportBufferFirstAt: number | null = null;
    let emailReportFlushTimer: ReturnType<typeof setTimeout> | null = null;
    const lastEmailSentByType = new Map<string, number>(); // Track last email time per violation type
    const rightClickCount = { count: 0, firstAt: 0 }; // Require 2+ right clicks before email
    const recentActions = new Map<string, number>(); // Track recent actions for pattern detection (select_all -> copy, etc.)

    const flushEmailReportBuffer = () => {
      if (emailReportBuffer.length === 0) return;
      
      const now = Date.now();
      const timeSinceFirst = emailReportBufferFirstAt ? now - emailReportBufferFirstAt : 0;
      
      // High-priority: always send email even for single violation (no "isolated" skip).
      // Includes devtools, save/capture, and all screenshot/record-related types.
      const highPriorityTypes = [
        'devtools_opened', 'f12_pressed', 'devtools_shortcut', 'save_page', 'view_source',
        'possible_screenshot_or_recording', 'suspicious_pattern', 'long_press', 'multi_touch',
        'page_hide', 'print_screen', 'print', 'touch_callout', 'window_focus_after_blur',
      ];
      const hasHighPriority = emailReportBuffer.some((v) =>
        highPriorityTypes.includes(String(v.violation_type || ''))
      );
      
      // If only 1 violation and it's been > 2 minutes since first, and it's not high-priority, skip email
      // (isolated attempt, likely accidental - still tracked in PostHog/DB)
      if (emailReportBuffer.length === 1 && timeSinceFirst > 120_000 && !hasHighPriority) {
        if (import.meta.env.DEV) {
          console.warn(`🔒 Skipping email: single isolated violation after ${Math.round(timeSinceFirst / 1000)}s (not high-priority)`);
        }
        // Clear buffer but don't send email
        emailReportBuffer.length = 0;
        emailReportBufferFirstAt = null;
        if (emailReportFlushTimer) {
          clearTimeout(emailReportFlushTimer);
          emailReportFlushTimer = null;
        }
        return;
      }
      
      // Send consolidated email with all violations
      const payload = { violations: [...emailReportBuffer] };
      
      // Update cooldown for all violation types in this batch
      const uniqueTypes = new Set(
        emailReportBuffer.map((v) => String(v.violation_type || 'unknown'))
      );
      uniqueTypes.forEach((type) => {
        lastEmailSentByType.set(type, now);
      });
      
      emailReportBuffer.length = 0;
      emailReportBufferFirstAt = null;
      if (emailReportFlushTimer) {
        clearTimeout(emailReportFlushTimer);
        emailReportFlushTimer = null;
      }
      
      if (import.meta.env.DEV) {
        console.warn(`🔒 Sending email report: ${payload.violations.length} violation(s) after ${Math.round(timeSinceFirst / 1000)}s`);
      }
      
      fetch('/api/send-protection-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch((e) => {
        if (import.meta.env.DEV) console.warn('Email report flush failed:', e);
      });
    };

    // Collect comprehensive device and location information
    const collectViolationData = async (violationType: string, additionalDetails?: Record<string, unknown>) => {
      const deviceInfo: Record<string, unknown> = {
        // Device type
        device_type: isMobile ? 'mobile' : 'desktop',
        platform: isIOS ? 'ios' : isAndroid ? 'android' : 'other',
        
        // User Agent
        user_agent: navigator.userAgent,
        user_agent_language: navigator.language,
        languages: navigator.languages?.join(',') || navigator.language,
        
        // Screen information
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        screen_avail_width: window.screen.availWidth,
        screen_avail_height: window.screen.availHeight,
        screen_color_depth: window.screen.colorDepth,
        screen_pixel_depth: (window.screen as unknown as { pixelDepth?: number }).pixelDepth || window.screen.colorDepth,
        device_pixel_ratio: window.devicePixelRatio || 1,
        
        // Viewport information
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        outer_width: window.outerWidth,
        outer_height: window.outerHeight,
        
        // Browser capabilities
        cookie_enabled: navigator.cookieEnabled,
        on_line: navigator.onLine,
        hardware_concurrency: navigator.hardwareConcurrency || null,
        max_touch_points: navigator.maxTouchPoints || 0,
        platform_info: (navigator as unknown as { platform?: string }).platform || navigator.userAgentData?.platform || 'unknown',
        
        // Timezone and locale
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezone_offset: new Date().getTimezoneOffset(),
        locale: Intl.DateTimeFormat().resolvedOptions().locale,
        
        // Connection information (if available)
        connection_type: (navigator as unknown as { connection?: { effectiveType?: string; downlink?: number; rtt?: number } }).connection?.effectiveType || null,
        connection_downlink: (navigator as unknown as { connection?: { effectiveType?: string; downlink?: number; rtt?: number } }).connection?.downlink || null,
        connection_rtt: (navigator as unknown as { connection?: { effectiveType?: string; downlink?: number; rtt?: number } }).connection?.rtt || null,
        
        // Memory information (if available)
        device_memory: (navigator as unknown as { deviceMemory?: number }).deviceMemory || null,
        
        // Referrer
        referrer: document.referrer || 'direct',
        
        // URL information
        current_url: window.location.href,
        current_path: window.location.pathname,
        current_host: window.location.host,
        current_protocol: window.location.protocol,
        
        // Timestamp
        violation_timestamp: new Date().toISOString(),
        violation_type: violationType,
        
        // Additional details
        ...additionalDetails
      };

      // Province: Worker uses full names (Buenos Aires, CABA, …); APIs use codes (BA, CB, …).
      // Prioritize Worker/cookie/storage over geo APIs; reports accept both.
      let userProvince: string | null = null;
      let provinceDetectionMethod: string | null = null;
      if (typeof window !== 'undefined') {
        const win = window as Window & {
          __TEMPLATE_CONFIG__?: {
            province?: string | null;
            provinceDetectionMethod?: string;
            latitude?: number | null;
            longitude?: number | null;
          };
        };
        const tc = win.__TEMPLATE_CONFIG__;
        if (tc?.province) {
          userProvince = tc.province;
          provinceDetectionMethod = tc.provinceDetectionMethod ?? 'worker_config';
        }
        if (!userProvince) {
          const match =
            document.cookie.match(/\buser_province=([^;]+)/) ||
            document.cookie.match(/\bcf_province=([^;]+)/);
          if (match) userProvince = match[1].trim() || null;
          if (userProvince) provinceDetectionMethod = provinceDetectionMethod ?? 'cookie';
        }
        if (!userProvince) {
          userProvince = localStorage.getItem('user_province') || localStorage.getItem('cf_province');
          if (userProvince) provinceDetectionMethod = provinceDetectionMethod ?? 'localStorage';
        }
        if (userProvince) {
          deviceInfo.province = userProvince;
          deviceInfo.user_province = userProvince;
        }
        if (provinceDetectionMethod) deviceInfo.provinceDetectionMethod = provinceDetectionMethod;
        if (tc?.latitude != null) deviceInfo.config_latitude = tc.latitude;
        if (tc?.longitude != null) deviceInfo.config_longitude = tc.longitude;
      }

      // Try to get geolocation data (cache to avoid multiple calls)
      if (!geoDataCache && !geoDataPromise) {
        geoDataPromise = (async () => {
          try {
            // Try Cloudflare geo endpoint first (if available)
            const geoResponse = await fetch('/api/geo', {
              method: 'GET',
              headers: {
                'Accept': 'application/json',
              },
            }).catch(() => null);
            
            if (geoResponse?.ok) {
              const geo = await geoResponse.json();
              const detection = geo.detection as Record<string, unknown> | undefined;
              geoDataCache = {
                country: geo.country,
                region: geo.region,
                city: geo.city,
                province: geo.province ?? geo.region,
                timezone: geo.timezone,
                latitude: geo.latitude,
                longitude: geo.longitude,
                asn: geo.asn,
                continent: geo.continent,
                geo_source: geo.source || 'cloudflare',
                ...(detection && { detection }),
              };
              return geoDataCache;
            }
            
            // Fallback: Try geolocate endpoint
            const geoLocateResponse = await fetch('/api/geolocate', {
              method: 'GET',
              headers: {
                'Accept': 'application/json',
              },
            }).catch(() => null);
            
            if (geoLocateResponse?.ok) {
              const geo = await geoLocateResponse.json();
              const detection = geo.detection as Record<string, unknown> | undefined;
              geoDataCache = {
                country: geo.country,
                region: geo.region,
                city: geo.city,
                province: geo.province ?? geo.region,
                timezone: geo.timezone,
                latitude: geo.latitude,
                longitude: geo.longitude,
                isp: geo.isp,
                geo_source: geo.source || 'geolocate_api',
                ...(detection && { detection }),
              };
              return geoDataCache;
            }
          } catch (error) {
            console.error('Error fetching geo data:', error);
          }
          return null;
        })();
      }
      
      // Wait for geo data if promise exists
      if (geoDataPromise) {
        try {
          const geoData = await geoDataPromise;
          if (geoData) {
            Object.assign(deviceInfo, geoData);
          }
        } catch (error) {
          // Silent fail - continue without geo data
        }
      } else if (geoDataCache) {
        Object.assign(deviceInfo, geoDataCache);
      }

      // Province: prefer config/cookie/storage over geo
      if (userProvince) {
        deviceInfo.province = userProvince;
        deviceInfo.user_province = userProvince;
      }
      // Attach geo detection metadata when from API (geoDataCache is set by fetch)
      const geoDetection = geoDataCache?.detection as Record<string, unknown> | undefined;
      if (geoDetection && typeof geoDetection === 'object') {
        deviceInfo.geo_detection = geoDetection;
        if (!deviceInfo.provinceDetectionMethod && geoDetection.provinceDetectionMethod) {
          deviceInfo.provinceDetectionMethod = geoDetection.provinceDetectionMethod;
        }
      }

      // Try to get IP from localStorage if previously stored
      const storedIP = localStorage.getItem('user_ip');
      if (storedIP) {
        deviceInfo.ip_address = storedIP;
      }

      // Get user region from localStorage
      const userRegion = localStorage.getItem('user_region');
      if (userRegion) {
        deviceInfo.user_region = userRegion;
      }

      return deviceInfo;
    };
    
    // Track protection violations with comprehensive data (fire-and-forget)
    // skipBuffer: when true, still send to PostHog/DB but do not add to email buffer (e.g. page_hide sync-add + flush).
    const trackProtectionViolation = (
      type: string,
      details?: Record<string, unknown>,
      opts?: { skipBuffer?: boolean }
    ) => {
      // Fire and forget - don't await to avoid blocking
      collectViolationData(type, details)
        .then((violationData) => {
          const payload = { ...violationData, page_path: effectivePagePath };
          // Send to PostHog with all collected data
          trackInvitacionGrupoPostHog('close', payload);

          // Also send to database via API endpoint for permanent storage
          fetch('/api/protection-violations', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          }).catch((dbError) => {
            // Silent fail for database - PostHog is primary tracking
            if (import.meta.env.DEV) {
              console.warn('Database tracking failed (non-critical):', dbError);
            }
          });

          if (opts?.skipBuffer) {
            if (import.meta.env.DEV) {
              console.warn('🔒 Protection Violation (skipBuffer):', { type, data: payload });
            }
            return;
          }

          // Email ONLY for real user attempts (no popup/toast - we track silently).
          //
          // DESKTOP-ONLY (DevTools / keyboard – NOT “saving video”):
          //   devtools_opened, f12_pressed, devtools_shortcut, console_shortcut, inspect_shortcut,
          //   view_source, save_page, print, print_screen, select_all, right_click
          // Console = opening DevTools or Ctrl+Shift+J etc. Users cannot “console_access” on iOS.
          //
          // iOS / MOBILE “save-like” (what we can detect):
          //   long_press — iOS “hold to save image”; Android long-press context menu.
          //   multi_touch — Pinch / multi-finger gestures (screenshot-like on some devices).
          //   touch_callout — iOS callout menu (“Save Image”, “Copy”, etc.).
          // We cannot detect: iOS screenshot (buttons), screen recording (Control Center).
          //
          // Excluded from email: console_access, page_hidden, page_visible_after_hidden, window_blur.
          // Include screenshot/record heuristics so email is sent for any detection.
          const criticalViolations = [
            'devtools_opened',
            'f12_pressed',
            'devtools_shortcut',
            'console_shortcut',
            'inspect_shortcut',
            'view_source',
            'save_page',
            'print',
            'print_screen',
            'select_all',
            'right_click',
            'long_press',
            'multi_touch',
            'touch_callout',
            'copy',
            'cut',
            'possible_screenshot_or_recording',
            'suspicious_pattern',
            'page_hide',
            'window_focus_after_blur',
          ];

          // Buffer critical violations for 3 minutes; send one consolidated email per user session.
          // This groups multiple attempts into a single report instead of sending one email per action.
          // Only send email for REAL save/capture attempts, not normal usage.
          if (criticalViolations.includes(type)) {
            const now = Date.now();
            const timeSinceLoad = now - pageLoadTime;
            const lastSent = lastEmailSentByType.get(type) ?? 0;
            const timeSinceLastSent = now - lastSent;

            // Grace period: no emails for first 90s (user needs time to interact normally)
            // Exception: devtools_opened requires 120s (very sensitive, often false positive on load)
            const gracePeriod = type === 'devtools_opened' ? 120_000 : EMAIL_GRACE_PERIOD_MS;
            if (timeSinceLoad < gracePeriod) {
              if (import.meta.env.DEV) {
                console.warn(`🔒 Skipping email for ${type} (grace period: ${Math.round(timeSinceLoad / 1000)}s / ${Math.round(gracePeriod / 1000)}s)`);
              }
              return; // Still tracked in PostHog/DB, just no email
            }

            // Pattern-based detection: require REAL save attempts, not accidental actions
            
            // select_all: Never email alone - only as part of select_all -> copy/cut pattern
            if (type === 'select_all') {
              recentActions.set('select_all', now);
              // Don't email for select_all alone - wait for copy/cut to follow
              if (import.meta.env.DEV) {
                console.warn(`🔒 Skipping email for ${type} (waiting for copy/cut pattern)`);
              }
              return; // Still tracked in PostHog/DB, but no email until copy/cut follows
            }

            // copy/cut: Only email if preceded by select_all within 3s (real save pattern: select -> copy)
            if (type === 'copy' || type === 'cut') {
              recentActions.set(type, now);
              const selectAllTime = recentActions.get('select_all') ?? 0;
              
              if (selectAllTime > 0 && now - selectAllTime <= 3000) {
                // This is a real save attempt (select_all -> copy/cut within 3s)
                // Continue to email - this is the pattern we want to catch
                if (import.meta.env.DEV) {
                  console.warn(`🔒 ✅ Real save pattern detected: select_all (${Math.round((now - selectAllTime) / 1000)}s ago) -> ${type}`);
                }
              } else {
                // Standalone copy/cut without select_all - likely accidental or normal use (copying text, etc.)
                if (import.meta.env.DEV) {
                  console.warn(`🔒 Skipping email for ${type} (no select_all pattern - standalone copy/cut)`);
                }
                return; // Still tracked, but no email - not a save attempt
              }
            }

            // right_click: require 2+ occurrences within 10s AND on image/visual content
            if (type === 'right_click') {
              const isImage = (details?.is_image as boolean) ?? false;
              const isVisual = (details?.is_visual as boolean) ?? false;
              
              // Only track if on image/visual (already filtered in handleContextMenu, but double-check)
              if (!isImage && !isVisual) {
                if (import.meta.env.DEV) {
                  console.warn(`🔒 Skipping email for ${type} (not on image/visual)`);
                }
                return; // Not a save attempt
              }
              
              if (now - rightClickCount.firstAt > 10_000) {
                rightClickCount.count = 0;
                rightClickCount.firstAt = now;
              }
              rightClickCount.count++;
              if (rightClickCount.count < 2) {
                if (import.meta.env.DEV) {
                  console.warn(`🔒 Skipping email for ${type} (need 2+ clicks, have ${rightClickCount.count})`);
                }
                return; // Still tracked in PostHog/DB, just no email
              }
              // Reset after 2nd click
              rightClickCount.count = 0;
              rightClickCount.firstAt = 0;
            }

            // devtools_opened: Only email if devtools_real_inspection flag is set (stayed open > 5s)
            if (type === 'devtools_opened') {
              const isRealInspection = (details?.devtools_real_inspection as boolean) ?? false;
              if (!isRealInspection) {
                // DevTools opened but closed before 5s - likely accidental, skip email
                if (import.meta.env.DEV) {
                  console.warn(`🔒 Skipping email for ${type} (closed before 5s or check_duration flag not set)`);
                }
                return; // Still tracked in PostHog/DB, but no email
              }
            }

            // Cooldown per type: if same violation type sent email recently, skip
            if (timeSinceLastSent < EMAIL_COOLDOWN_BY_TYPE_MS) {
              if (import.meta.env.DEV) {
                console.warn(`🔒 Skipping email for ${type} (cooldown: ${Math.round(timeSinceLastSent / 1000)}s ago)`);
              }
              return; // Still tracked in PostHog/DB, just no email
            }

            emailReportBuffer.push(payload);
            lastEmailSentByType.set(type, now);
            if (emailReportBufferFirstAt === null) {
              emailReportBufferFirstAt = now;
              emailReportFlushTimer = setTimeout(flushEmailReportBuffer, EMAIL_REPORT_BUFFER_MS);
            }
          }
          
          // Also log to console in dev mode for debugging
          if (import.meta.env.DEV) {
            console.warn('🔒 Protection Violation Detected:', {
              type,
              data: payload
            });
          }
        })
        .catch((e) => {
          // Silent fail - don't break the app if tracking fails
          if (import.meta.env.DEV) {
            console.error('Error tracking protection violation:', e);
          }
        });
    };

    // Disable right-click context menu
    const handleContextMenu = (e: MouseEvent) => {
      // Allow right-click on video player (user might need controls)
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'VIDEO' ||
        target.closest('.mux-video-player-cta') ||
        target.closest('video')
      ) {
        return; // Don't track or prevent on video player
      }
      
      // Only track right-click on images or visual content areas (real save attempt)
      // Exclude: text, buttons, links (normal interaction)
      const isImage = target.tagName === 'IMG' || target.closest('img');
      const isVisualContent = target.closest('[data-content-type="visual"]') || 
                              target.style.backgroundImage ||
                              (target as HTMLElement).offsetWidth > 200 && (target as HTMLElement).offsetHeight > 200;
      
      // If not on image/visual content, allow normal right-click (text selection, etc.)
      if (!isImage && !isVisualContent) {
        return; // Don't prevent or track - normal text interaction
      }
      
      e.preventDefault();
      e.stopPropagation();
      trackProtectionViolation('right_click', {
        target_tag: target.tagName,
        is_image: isImage,
        is_visual: isVisualContent,
      });
      return false;
    };

    // Disable text selection (but allow on video player for controls)
    const handleSelectStart = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'VIDEO' || target.closest('.mux-video-player-cta') || target.closest('video')) {
        return; // Allow text selection in video player
      }
      e.preventDefault();
      trackProtectionViolation('text_selection');
      return false;
    };

    // Disable drag
    const handleDragStart = (e: DragEvent) => {
      e.preventDefault();
      return false;
    };

    // Disable copy, cut, paste
    const handleCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      trackProtectionViolation('copy');
      return false;
    };

    const handleCut = (e: ClipboardEvent) => {
      e.preventDefault();
      trackProtectionViolation('cut');
      return false;
    };

    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault();
      trackProtectionViolation('paste');
      return false;
    };

    // Disable keyboard shortcuts
    const handleKeyDown = (e: KeyboardEvent) => {
      // Disable F12 (DevTools)
      if (e.key === 'F12') {
        e.preventDefault();
        trackProtectionViolation('f12_pressed');
        return false;
      }

      // Disable Ctrl+Shift+I (DevTools)
      if (e.ctrlKey && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        trackProtectionViolation('devtools_shortcut');
        return false;
      }

      // Disable Ctrl+Shift+J (Console)
      if (e.ctrlKey && e.shiftKey && e.key === 'J') {
        e.preventDefault();
        trackProtectionViolation('console_shortcut');
        return false;
      }

      // Disable Ctrl+Shift+C (Inspect Element)
      if (e.ctrlKey && e.shiftKey && e.key === 'C') {
        e.preventDefault();
        trackProtectionViolation('inspect_shortcut');
        return false;
      }

      // Disable Ctrl+U (View Source)
      if (e.ctrlKey && e.key === 'u') {
        e.preventDefault();
        trackProtectionViolation('view_source');
        return false;
      }

      // Disable Ctrl+S (Save Page)
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        trackProtectionViolation('save_page');
        return false;
      }

      // Disable Ctrl+P (Print)
      if (e.ctrlKey && e.key === 'p') {
        e.preventDefault();
        trackProtectionViolation('print');
        return false;
      }

      // Disable Print Screen (limited browser support)
      if (e.key === 'PrintScreen' || (e.ctrlKey && e.shiftKey && e.key === 'S')) {
        e.preventDefault();
        trackProtectionViolation('print_screen');
        return false;
      }

      // Disable Ctrl+A (Select All)
      if (e.ctrlKey && e.key === 'a') {
        e.preventDefault();
        trackProtectionViolation('select_all');
        return false;
      }
    };

    // Detect DevTools opening (only if size changed AFTER page load, not initial state)
    // Only email if DevTools stays open > 5s (real inspection attempt, not accidental)
    let devToolsOpen = false;
    let devToolsOpenedAt: number | null = null;
    let devToolsEmailSent = false;
    const DEVTOOLS_MIN_DURATION_MS = 5000; // Require 5s open before email
    
    const detectDevTools = () => {
      const threshold = 160;
      const currentWidthDiff = window.outerWidth - window.innerWidth;
      const currentHeightDiff = window.outerHeight - window.innerHeight;
      const initialWidthDiff = initialWindowSize.outerWidth - initialWindowSize.innerWidth;
      const initialHeightDiff = initialWindowSize.outerHeight - initialWindowSize.innerHeight;
      
      // Only trigger if: (a) current diff > threshold, AND (b) it's significantly different from initial
      const widthChanged = Math.abs(currentWidthDiff - initialWidthDiff) > 100;
      const heightChanged = Math.abs(currentHeightDiff - initialHeightDiff) > 100;
      const exceedsThreshold = currentWidthDiff > threshold || currentHeightDiff > threshold;
      
      if (exceedsThreshold && (widthChanged || heightChanged)) {
        if (!devToolsOpen) {
          devToolsOpen = true;
          devToolsOpenedAt = Date.now();
          devToolsEmailSent = false;
          // Track immediately (for PostHog/DB), but email will be sent later if stays open > 5s
          trackProtectionViolation('devtools_opened', {
            width_diff: currentWidthDiff,
            height_diff: currentHeightDiff,
            initial_width_diff: initialWidthDiff,
            initial_height_diff: initialHeightDiff,
            devtools_opened_at: devToolsOpenedAt,
            devtools_check_duration: true, // Flag: email only if duration > 5s
          });
        } else if (devToolsOpenedAt && !devToolsEmailSent) {
          // DevTools still open - check if duration > 5s
          const duration = Date.now() - devToolsOpenedAt;
          if (duration >= DEVTOOLS_MIN_DURATION_MS) {
            devToolsEmailSent = true;
            // Send email now (DevTools stayed open > 5s = real inspection attempt)
            trackProtectionViolation('devtools_opened', {
              width_diff: currentWidthDiff,
              height_diff: currentHeightDiff,
              initial_width_diff: initialWidthDiff,
              initial_height_diff: initialHeightDiff,
              devtools_opened_at: devToolsOpenedAt,
              devtools_duration_ms: duration,
              devtools_real_inspection: true, // Flag: this is a real attempt
            });
          }
        }
      } else {
        if (devToolsOpen && devToolsOpenedAt && !devToolsEmailSent) {
          // DevTools closed before 5s - was likely accidental, don't email
          if (import.meta.env.DEV) {
            const duration = Date.now() - devToolsOpenedAt;
            console.warn(`🔒 DevTools closed before 5s (${Math.round(duration / 1000)}s) - skipping email`);
          }
        }
        devToolsOpen = false;
        devToolsOpenedAt = null;
        devToolsEmailSent = false;
      }
    };

    // NOTE: We do NOT override console - PostHog, GA, and other analytics use it.
    // Overriding caused massive false positives (console_access) and email spam.
    // We never show popups/alerts to users ("can't screenshot") - we only track silently.

    // Mobile-specific: Prevent long press (iOS Safari save image, Android context menu)
    let longPressTimer: NodeJS.Timeout | null = null;
    const handleTouchStart = (e: TouchEvent) => {
      longPressTimer = setTimeout(() => {
        trackProtectionViolation('long_press');
        e.preventDefault();
        e.stopPropagation();
        // Vibrate if available (Android)
        if (navigator.vibrate) {
          navigator.vibrate(200);
        }
      }, 500); // 500ms = long press threshold
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    };

    // Mobile: Prevent image save on long press (iOS Safari)
    const handleTouchCancel = (e: TouchEvent) => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
      trackProtectionViolation('touch_cancel');
    };

    // iOS Safari: Prevent callout menu (long-press → "Save Image", "Copy", etc.)
    const handleTouchCallout = (e: TouchEvent) => {
      e.preventDefault();
      trackProtectionViolation('touch_callout');
    };

    // Detect app switching (iOS/Android - possible screenshot)
    const handlePageHide = () => {
      logScreenshotPatternEvent('pagehide');
      // Sync-add page_hide to buffer so it's included in flush (async trackProtectionViolation runs after flush).
      const now = Date.now();
      const minimalPageHide: Record<string, unknown> = {
        violation_type: 'page_hide',
        violation_timestamp: new Date().toISOString(),
        current_url: typeof window !== 'undefined' ? window.location.href : '',
        current_path: typeof window !== 'undefined' ? window.location.pathname : effectivePagePath,
        page_path: effectivePagePath,
        timestamp: now,
        device_type: isMobile ? 'mobile' : 'desktop',
        platform: isIOS ? 'ios' : isAndroid ? 'android' : 'other',
      };
      emailReportBuffer.push(minimalPageHide);
      if (emailReportBufferFirstAt === null) {
        emailReportBufferFirstAt = now;
      }
      lastEmailSentByType.set('page_hide', now);
      if (emailReportFlushTimer) {
        clearTimeout(emailReportFlushTimer);
        emailReportFlushTimer = null;
      }
      flushEmailReportBuffer();
      trackProtectionViolation('page_hide', { timestamp: now }, { skipBuffer: true });
    };

    const handlePageShow = () => {
      trackProtectionViolation('page_show', {
        timestamp: Date.now()
      });
    };

    // Best-effort screenshot/screen-recording heuristics (MOBILE ONLY, low confidence).
    // No web API exists; iOS/Android use native-only. We use short hidden/blur + pattern analysis.
    // Tab switch = usually 1s+ hidden/blur; screenshot UI or Control Center = often <500ms.
    // Pattern (Apple-style): blur + visibility_change (hidden) within 2s → suspicious.
    const POSSIBLE_CAPTURE_COOLDOWN_MS = 5000;
    const PATTERN_WINDOW_MS = 2000;
    const PATTERN_EVENTS_RETENTION_MS = 30000;
    let lastPossibleCaptureAt = 0;
    let visibilityHiddenAt: number | null = null;

    // Session watermark (dynamic, updated every 1s) for attribution if content leaks
    const sessionId = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `s${Date.now().toString(36)}`;
    let lastWatermarkValue = `${BRAND_PERSON_NAME} | ${sessionId.slice(0, 8)} | ${Date.now()}`;
    const getCurrentWatermark = () => lastWatermarkValue;

    // Event buffer for pattern analysis (blur + visibility_change within 2s)
    type PatternEvent = { type: string; timestamp: number; data?: unknown };
    const patternEvents: PatternEvent[] = [];
    const logScreenshotPatternEvent = (type: string, data?: unknown) => {
      const ev: PatternEvent = { type, timestamp: Date.now(), data };
      patternEvents.push(ev);
      const cutoff = Date.now() - PATTERN_EVENTS_RETENTION_MS;
      while (patternEvents.length > 0 && patternEvents[0].timestamp < cutoff) {
        patternEvents.shift();
      }
      analyzeScreenshotPattern();
    };

    const analyzeScreenshotPattern = () => {
      if (!isMobile) return;
      const now = Date.now();
      if (now - lastPossibleCaptureAt < POSSIBLE_CAPTURE_COOLDOWN_MS) return;
      const recent = patternEvents.filter((e) => now - e.timestamp < PATTERN_WINDOW_MS);
      const hasBlur = recent.some((e) => e.type === 'blur');
      const hasVisibilityHidden = recent.some(
        (e) => e.type === 'visibility_change' && (e.data as { hidden?: boolean })?.hidden === true
      );
      if (hasBlur && hasVisibilityHidden) {
        lastPossibleCaptureAt = now;
        const platform = isIOS ? 'ios' : isAndroid ? 'android' : 'mobile';
        trackProtectionViolation('suspicious_pattern', {
          heuristic: 'pattern_blur_visibility',
          recent_events: recent.slice(-10).map(({ type, timestamp, data }) => ({ type, timestamp, data })),
          current_watermark: getCurrentWatermark(),
          confidence: 'low',
          platform,
          timestamp: now,
        });
      }
    };

    const tryTrackPossibleScreenshotOrRecording = (
      type: 'short_visibility_hidden' | 'short_blur',
      durationMs: number
    ) => {
      if (!isMobile) return;
      const now = Date.now();
      if (now - lastPossibleCaptureAt < POSSIBLE_CAPTURE_COOLDOWN_MS) return;
      lastPossibleCaptureAt = now;
      const platform = isIOS ? 'ios' : isAndroid ? 'android' : 'mobile';
      const recent = patternEvents.filter((e) => now - e.timestamp < PATTERN_WINDOW_MS);
      trackProtectionViolation('possible_screenshot_or_recording', {
        heuristic: type,
        duration_ms: durationMs,
        confidence: 'low',
        platform,
        timestamp: now,
        recent_events: recent.slice(-10).map(({ type: t, timestamp: ts, data }) => ({ type: t, timestamp: ts, data })),
        current_watermark: getCurrentWatermark(),
      });
    };

    // Page Visibility: track hidden-at, then on visible check duration
    let lastVisibilityState = document.visibilityState;
    const handleVisibilityChangeEnhanced = () => {
      if (document.hidden) {
        visibilityHiddenAt = Date.now();
        logScreenshotPatternEvent('visibility_change', { hidden: true });
        trackProtectionViolation('page_hidden', {
          visibility_state: document.visibilityState,
          timestamp: Date.now()
        });
      } else if (lastVisibilityState === 'hidden' && document.visibilityState === 'visible') {
        logScreenshotPatternEvent('visibility_change', { hidden: false });
        const now = Date.now();
        const hiddenDuration = visibilityHiddenAt != null ? now - visibilityHiddenAt : 0;
        visibilityHiddenAt = null;
        trackProtectionViolation('page_visible_after_hidden', {
          timestamp: now,
          hidden_duration_ms: hiddenDuration
        });
        // Best-effort: very short hidden on mobile → possible screenshot (UI overlay) or recording start
        if (hiddenDuration > 0 && hiddenDuration < 500) {
          tryTrackPossibleScreenshotOrRecording('short_visibility_hidden', hiddenDuration);
        }
      }
      lastVisibilityState = document.visibilityState;
    };

    // Blur/focus: very short blur on mobile → possible screenshot or recording UI
    let focusTime = Date.now();
    const handleFocus = () => {
      logScreenshotPatternEvent('focus');
      const blurDuration = Date.now() - focusTime;
      if (blurDuration > 1000) {
        trackProtectionViolation('window_focus_after_blur', {
          blur_duration: blurDuration
        });
      } else if (blurDuration > 0 && blurDuration < 500) {
        tryTrackPossibleScreenshotOrRecording('short_blur', blurDuration);
      }
    };

    const handleBlurEnhanced = () => {
      focusTime = Date.now();
      logScreenshotPatternEvent('blur');
      trackProtectionViolation('window_blur');
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('cut', handleCut);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('visibilitychange', handleVisibilityChangeEnhanced);
    window.addEventListener('blur', handleBlurEnhanced);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);

    // Mobile-specific touch events
    if (isMobile) {
      document.addEventListener('touchstart', handleTouchStart, { passive: false });
      document.addEventListener('touchend', handleTouchEnd, { passive: false });
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchcancel', handleTouchCancel, { passive: false });
    }
    
    // iOS Safari specific: Prevent text selection magnifier and multi-touch
    // Define early so cleanup can access it
    const handleIOSTouchStart = (e: TouchEvent) => {
      if ((e.target as HTMLElement).tagName === 'VIDEO' || 
          (e.target as HTMLElement).closest('.mux-video-player-cta')) {
        // Allow video player interactions
        return;
      }
      // Prevent default for other elements
      // Detect multi-touch (pinch to zoom, screenshot gestures)
      if (e.touches && e.touches.length > 1) {
        e.preventDefault();
        trackProtectionViolation('multi_touch', {
          touch_count: e.touches.length
        });
      }
    };

    // Monitor DevTools
    const devToolsInterval = setInterval(detectDevTools, 500);

    // Add CSS to prevent text selection (but allow video player interaction)
    const style = document.createElement('style');
    style.id = 'screenshot-protection-styles';
    style.textContent = `
      * {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
        -webkit-touch-callout: none !important;
        -webkit-tap-highlight-color: transparent !important;
      }
      img {
        -webkit-user-drag: none !important;
        -khtml-user-drag: none !important;
        -moz-user-drag: none !important;
        -o-user-drag: none !important;
        user-drag: none !important;
      }
      /* Allow video player controls to work */
      video, .mux-video-player-cta, .mux-video-player-cta * {
        -webkit-user-drag: none !important;
        user-drag: none !important;
        pointer-events: auto !important;
      }
      /* Mobile: Prevent long press on images and videos */
      img, video {
        -webkit-touch-callout: none !important;
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
      }
      /* iOS Safari: Prevent text selection magnifier */
      @supports (-webkit-touch-callout: none) {
        * {
          -webkit-touch-callout: none !important;
        }
      }
      /* Android: Prevent context menu on long press */
      @media (hover: none) and (pointer: coarse) {
        * {
          -webkit-tap-highlight-color: transparent !important;
          touch-action: manipulation !important;
        }
      }
      /* Watermark overlay - contained within video area */
      .screenshot-protection-watermark {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 20;
        overflow: hidden;
        background: repeating-linear-gradient(
          45deg,
          transparent,
          transparent 100px,
          rgba(255, 0, 0, 0.03) 100px,
          rgba(255, 0, 0, 0.03) 200px
        );
        background-size: 200px 200px;
      }
      .screenshot-protection-watermark::before,
      .screenshot-protection-watermark .watermark-dynamic {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) rotate(-45deg);
        font-size: clamp(1.5rem, 4vw, 3rem);
        font-weight: bold;
        color: rgba(255, 255, 255, 0.08);
        white-space: nowrap;
        pointer-events: none;
        user-select: none;
        max-width: 90vw;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .screenshot-protection-watermark::before {
        content: "${BRAND_PERSON_NAME} - Contenido Exclusivo";
      }
      .screenshot-protection-watermark .watermark-dynamic {
        margin-top: 1.2em;
        font-size: clamp(0.75rem, 2vw, 1.25rem);
        opacity: 0.7;
      }
      /* Ensure watermark stays within video bounds on desktop reel mode */
      @media (min-width: 768px) {
        .screenshot-protection-watermark {
          left: 50%;
          transform: translateX(-50%);
          width: min(56.25vh, 90vw);
          max-width: min(56.25vh, 90vw);
        }
        @media (min-width: 1920px) {
          .screenshot-protection-watermark {
            width: min(56.25vh, 1200px);
            max-width: min(56.25vh, 1200px);
          }
        }
      }
    `;
    document.head.appendChild(style);

    // Add watermark overlay (static ::before + dynamic span: sessionId + timestamp, updated every 1s)
    const watermark = document.createElement('div');
    watermark.className = 'screenshot-protection-watermark';
    watermark.setAttribute('aria-hidden', 'true');
    const watermarkSpan = document.createElement('span');
    watermarkSpan.className = 'watermark-dynamic';
    watermarkSpan.setAttribute('data-watermark', '');
    const updateWatermark = () => {
      const v = `${BRAND_PERSON_NAME} | ${sessionId.slice(0, 8)} | ${Date.now()}`;
      lastWatermarkValue = v;
      watermarkSpan.textContent = v;
      watermarkSpan.setAttribute('data-watermark', v);
    };
    updateWatermark();
    watermark.appendChild(watermarkSpan);
    document.body.appendChild(watermark);
    const watermarkInterval = setInterval(updateWatermark, 1000);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('cut', handleCut);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('visibilitychange', handleVisibilityChangeEnhanced);
      window.removeEventListener('blur', handleBlurEnhanced);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      
      if (isMobile) {
        document.removeEventListener('touchstart', handleTouchStart);
        document.removeEventListener('touchend', handleTouchEnd);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchcancel', handleTouchCancel);
      }
      
      if (isIOS && typeof handleIOSTouchStart !== 'undefined') {
        document.removeEventListener('touchstart', handleIOSTouchStart);
      }
      
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
      clearInterval(devToolsInterval);
      clearInterval(watermarkInterval);
      if (emailReportFlushTimer) {
        clearTimeout(emailReportFlushTimer);
        emailReportFlushTimer = null;
      }
      flushEmailReportBuffer();

      // Remove watermark
      const existingWatermark = document.querySelector('.screenshot-protection-watermark');
      if (existingWatermark) {
        document.body.removeChild(existingWatermark);
      }
      
      // Remove styles
      const existingStyle = document.getElementById('screenshot-protection-styles');
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, [pagePath]);

  // Block body scroll (same as BaseTemplate - especially important for Instagram browser)
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalWidth = document.body.style.width;
    const originalHeight = document.body.style.height;
    
    // Block scroll - especially important in Instagram browser
    if (instagramOpts.isInstagramBrowser) {
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
    }
    document.body.style.overflow = 'hidden';
    
    // Cleanup function to restore scroll
    return () => {
      document.body.style.overflow = originalOverflow;
      if (instagramOpts.isInstagramBrowser) {
        document.body.style.position = originalPosition;
        document.body.style.width = originalWidth;
        document.body.style.height = originalHeight;
      }
    };
  }, [instagramOpts.isInstagramBrowser]);

  // CTA click handler
  const handleCTAClick = useCallback(
    (url: string) => {
      trackInvitacionGrupoPostHog('cta_click', { cta_url: url, page_path: pagePath });
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [pagePath]
  );

  // Simple, direct render - no complex state management
  // Note: onClose is not passed, so the close button (X) will be hidden
  return (
    <ErrorBoundary>
      <main
        className="fixed inset-0 w-full h-full overflow-hidden bg-black"
        role="main"
        aria-label="Telegram Groups"
      >
        <MuxVideoPlayerWithCTA
          page={page}
          openInNewTab
          displayMode="reel"
          loop
          onCTAClick={handleCTAClick}
          containerClassName="w-full h-full min-h-full"
        />
      </main>
    </ErrorBoundary>
  );
};

export default InvitacionGrupo;
